import { Dropbox } from 'dropbox';

export class DropboxService {
  private static instance: DropboxService;
  private dbx: Dropbox | null = null;

  private constructor() {
    try {
      const accessToken = localStorage.getItem('dropboxAccessToken');
      if (accessToken) {
        this.dbx = new Dropbox({ accessToken, fetch: window.fetch.bind(window) });
      }
      // If no token, we just leave dbx as null and handle it in the methods
    } catch (error) {
      console.error('Error initializing DropboxService:', error);
      // Just allow the service to initialize with dbx as null
    }
  }

  public static getInstance(): DropboxService {
    if (!DropboxService.instance) {
      try {
        DropboxService.instance = new DropboxService();
      } catch (error) {
        console.error('Failed to create DropboxService instance:', error);
        // Create a fallback instance that will mark all operations as requiring auth
        DropboxService.instance = new DropboxService();
      }
    }
    return DropboxService.instance;
  }

  public isAuthenticated(): boolean {
    return !!localStorage.getItem('dropboxAccessToken') && !!this.dbx;
  }

  public async authenticate(): Promise<void> {
    const CLIENT_ID = '7avzllilqvi9a9p';
    // Use the simple root redirect
    const redirectUri = window.location.origin + '/';
    const authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${CLIENT_ID}&response_type=token&redirect_uri=${redirectUri}`;
    window.location.href = authUrl;
  }

  public async getPermanentLinks(files: { id: string; name: string; link: string }[]): Promise<{ url: string }[]> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated with Dropbox');
    }

    return files.map(file => {
      // Convert Dropbox preview links to direct download links by adjusting the URL
      // For standard links like https://www.dropbox.com/s/abc123/file.mp3?dl=0
      // Change to https://dl.dropboxusercontent.com/s/abc123/file.mp3?dl=1
      
      const originalUrl = file.link;
      let url = file.link;
      
      console.log(`[DropboxService] Converting URL for file: ${file.name}`);
      
      // If we have a shared link URL, modify it for direct download
      if (url.includes('dropbox.com')) {
        const beforeConversion = url;
        
        // Replace www.dropbox.com with dl.dropboxusercontent.com
        url = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
        
        // Ensure the dl parameter is set to 1
        if (url.includes('?')) {
          // URL already has parameters
          if (url.includes('dl=0')) {
            url = url.replace('dl=0', 'dl=1');
          } else if (!url.includes('dl=')) {
            url += '&dl=1';
          }
        } else {
          // URL has no parameters yet
          url += '?dl=1';
        }
        
        console.log(`[DropboxService] Converted URL for ${file.name}`);
      }
      
      return { url };
    });
  }

  public async getFileContent(
    fileId: string, 
    url: string, 
    onProgress?: (bytesDownloaded: number, totalBytes: number) => void
  ): Promise<Blob> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated with Dropbox');
    }

    try {
      // Try multiple fetch strategies to improve reliability on mobile browsers
      let response;
      let lastError;
      
      // Strategy 1: Standard fetch with optimized headers for mobile
      try {
        response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          cache: 'default',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
        });
      } catch (fetchError) {
        lastError = fetchError;
        console.error(`[DropboxService] Strategy 1 failed for ${fileId}:`, fetchError);
        
        // Strategy 2: Retry with different cache policy
        try {
          await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay
          response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            cache: 'force-cache',
            credentials: 'omit'
          });
        } catch (retryError) {
          lastError = retryError;
          console.error(`[DropboxService] Strategy 2 failed for ${fileId}:`, retryError);
          
          // Strategy 3: Final attempt with minimal options
          try {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Longer delay
            response = await fetch(url);
          } catch (finalError) {
            console.error(`[DropboxService] All fetch attempts failed for ${fileId}:`, finalError);
            throw new Error(`Failed to download ${fileId}: Network error. Try fewer files or check connection.`);
          }
        }
      }
      
      if (!response.ok) {
        const errorMsg = `Failed to fetch file content: ${response.status} ${response.statusText}`;
        console.error(`[DropboxService] HTTP error for ${fileId}:`, errorMsg);
        throw new Error(`Download failed for ${fileId}: ${errorMsg}`);
      }

      // If no progress callback is provided, fall back to simple blob download
      if (!onProgress || !response.body) {
        const blob = await response.blob();
        console.log(`[DropboxService] Successfully downloaded ${fileId}: ${(blob.size / 1024 / 1024).toFixed(1)}MB`);
        return blob;
      }

      // Real-time progress tracking using ReadableStream
      const reader = response.body.getReader();
      const contentLength = response.headers.get('content-length');
      const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;
      
      let downloadedBytes = 0;
      const chunks: Uint8Array[] = [];
      
      console.log(`[DropboxService] Starting progressive download for ${fileId}: ${totalBytes > 0 ? (totalBytes / 1024 / 1024).toFixed(1) + 'MB' : 'unknown size'}`);
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          chunks.push(value);
          downloadedBytes += value.length;
          
          // Report progress if we have a total size
          if (onProgress && totalBytes > 0) {
            onProgress(downloadedBytes, totalBytes);
          }
        }
        
        const blob = new Blob(chunks);
        console.log(`[DropboxService] Successfully downloaded ${fileId}: ${(blob.size / 1024 / 1024).toFixed(1)}MB`);
        return blob;
        
      } catch (streamError) {
        console.error(`[DropboxService] Stream reading failed for ${fileId}:`, streamError);
        // Fall back to simple blob download if streaming fails
        const blob = await response.blob();
        console.log(`[DropboxService] Fallback download completed for ${fileId}: ${(blob.size / 1024 / 1024).toFixed(1)}MB`);
        return blob;
      }
      
    } catch (error) {
      console.error(`[DropboxService] Error fetching file content for ${fileId}:`, error);
      throw error;
    }
  }
}